import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const DEFAULT_SITE_URL = 'https://www.alvarofitness.com';
const DEFAULT_TITLE = 'Alvaro Fitness | Personal Training & Online Coaching';
const DEFAULT_DESCRIPTION =
  'Personal training in Dallas and online coaching with structured programming, nutrition guidance, and measurable results.';

const getRouteSEO = (pathname) => {
  if (pathname === '/') {
    return {
      title: 'Alvaro Fitness | Dallas Personal Training & Online Coaching',
      description:
        'Premium personal training in Dallas and online coaching programs with structured training and nutrition support.',
    };
  }

  if (pathname === '/onsite') {
    return {
      title: 'Personal Trainer in Dallas | Alvaro Fitness',
      description:
        'In-person personal training in Dallas with customized workouts, accountability, and coaching for long-term results.',
    };
  }

  if (pathname === '/online') {
    return {
      title: 'Online Coaching | Alvaro Fitness',
      description:
        'Remote coaching with personalized programming, nutrition support, and progress tracking through the app.',
    };
  }

  if (pathname === '/studio') {
    return {
      title: 'AF Studio | Alvaro Fitness',
      description:
        'Train live with Alvaro or on demand: 3 live classes a week including Rep Lab rep counting, follow-along workouts and class replays. Try a free workout.',
    };
  }

  if (pathname === '/8-week-program') {
    return {
      title: '8-Week Reset Program | Alvaro Fitness',
      description:
        'Kickstart your transformation with the 8-Week Reset program, including structured training and nutrition guidance.',
    };
  }

  if (pathname === '/12-week-program') {
    return {
      title: '12-Week Hypertrophy System | Alvaro Fitness',
      description:
        'A periodized 12-week Upper/Lower hypertrophy program across three escalating phases — structured training, nutrition guidance, and app-based tracking.',
    };
  }

  if (pathname === '/programs') {
    return {
      title: 'Self-Guided Programs | Alvaro Fitness',
      description:
        'Browse self-guided training programs — the 8-Week Reset and the 12-Week System — with structured programming, nutrition, and video demos.',
    };
  }

  if (pathname === '/program-checkout') {
    return {
      title: 'Program Checkout | Alvaro Fitness',
      description: 'Complete your program purchase securely.',
      noindex: true,
    };
  }

  if (pathname === '/program/success' || pathname === '/training/success') {
    return {
      title: 'Success | Alvaro Fitness',
      description: 'Your purchase was successful. Welcome to your program.',
      noindex: true,
    };
  }

  if (pathname === '/checkout/success' || pathname === '/checkout/canceled') {
    return {
      title: 'Checkout | Alvaro Fitness',
      description: 'Stripe checkout status page.',
      noindex: true,
    };
  }

  if (pathname === '/subscription/success') {
    return {
      title: 'Subscription Success | Alvaro Fitness',
      description: 'Your subscription is now active.',
      noindex: true,
    };
  }

  if (pathname === '/free-meal-plan') {
    return {
      title: 'Free Macro Calculator + Meal Plan | Alvaro Fitness',
      description:
        'Get your personal macros and a done-for-you day of meals — swaps and a shopping list included. Free, in about a minute.',
    };
  }

  if (pathname === '/6-week-challenge') {
    return {
      title: '6-Week Challenge | Alvaro Fitness',
      description:
        'The next 6-Week Challenge starts soon. Join the waitlist for updates on when it starts — and first pick of a spot.',
    };
  }

  if (pathname === '/links') {
    return {
      title: 'Alvaro Fitness | Links',
      description:
        'Everything from Alvaro Fitness in one place: online coaching, the 8-Week Reset, AF Studio, and a free workout plan and meal plan.',
    };
  }

  if (pathname === '/free') {
    return {
      title: 'Free Workouts + Meal Plan | Alvaro Fitness',
      description:
        'A free 4-week dumbbell plan, and your own macros with a full day of meals. Pick one or both and get them by email.',
    };
  }

  if (pathname === '/reset-starter') {
    return {
      title: 'Free Dumbbells-Only Starter | Alvaro Fitness',
      description:
        'Three dumbbell workouts, once a week for four weeks — your free first step into the 6-Week RESET Challenge.',
    };
  }

  if (pathname === '/apply') {
    return {
      title: 'Apply for 1-on-1 Coaching | Alvaro Fitness',
      description:
        'A short application for 1-on-1 online coaching with Alvaro — a few quick questions to see if it is the right fit.',
    };
  }

  if (
    pathname.startsWith('/nutrition') ||
    pathname.startsWith('/dashboard/nutrition')
  ) {
    return {
      title: 'Nutrition Calculator | Alvaro Fitness',
      description:
        'Calculate macros and generate meal plan guidance based on your goals.',
      noindex: pathname.startsWith('/dashboard/'),
    };
  }

  if (pathname.startsWith('/videos') || pathname.startsWith('/watch-video')) {
    return {
      title: 'Video Library | Alvaro Fitness',
      description:
        'Browse workout and technique videos to support your training plan.',
    };
  }

  if (pathname.startsWith('/watch-stream') || pathname === '/live-stream') {
    return {
      title: 'Live Stream | Alvaro Fitness',
      description: 'Join live training sessions.',
      noindex: true,
    };
  }

  if (
    pathname.startsWith('/dashboard') ||
    pathname.startsWith('/admin') ||
    pathname === '/account' ||
    pathname === '/booking' ||
    pathname === '/create-recording' ||
    pathname === '/create-scheduled-stream' ||
    pathname === '/create-interval-timer' ||
    pathname === '/meals/orders' ||
    pathname === '/meals/orders-detail' ||
    pathname === '/meals/manage-meals'
  ) {
    return {
      title: 'Dashboard | Alvaro Fitness',
      description: 'Account and training management pages.',
      noindex: true,
    };
  }

  // Meal prep is a private page for current clients: keep it out of search.
  if (pathname === '/meals' || pathname.startsWith('/meals/')) {
    return {
      title: 'Meal Prep | Alvaro Fitness',
      description: 'Order this week’s meal prep or manage your weekly meal plan.',
      noindex: true,
    };
  }

  if (
    pathname === '/login' ||
    pathname === '/register' ||
    pathname === '/reset-password'
  ) {
    return {
      title: 'Account Access | Alvaro Fitness',
      description: 'Log in or create your Alvaro Fitness account.',
      noindex: true,
    };
  }

  if (pathname === '/contact') {
    return {
      title: 'Contact | Alvaro Fitness',
      description:
        'Contact Alvaro Fitness for coaching, training, and program support.',
    };
  }

  if (pathname === '/next-steps') {
    return {
      title: 'Next Steps | Alvaro Fitness',
      description: 'Review your next steps to start training with confidence.',
    };
  }

  if (pathname === '/unauthorized') {
    return {
      title: 'Unauthorized | Alvaro Fitness',
      description: 'You do not have permission to view this page.',
      noindex: true,
    };
  }

  return {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  };
};

export default function RouteSEO() {
  const { pathname } = useLocation();
  const siteUrl = process.env.REACT_APP_APP_URL || DEFAULT_SITE_URL;

  const { title, description, noindex } = useMemo(
    () => getRouteSEO(pathname),
    [pathname],
  );

  const canonicalUrl = `${siteUrl}${pathname}`;

  return (
    <Helmet>
      <html lang='en' />
      <title>{title}</title>
      <meta name='description' content={description} />
      <meta
        name='robots'
        content={noindex ? 'noindex, nofollow' : 'index, follow'}
      />
      <link rel='canonical' href={canonicalUrl} />
      <meta property='og:title' content={title} />
      <meta property='og:description' content={description} />
      <meta property='og:url' content={canonicalUrl} />
      <meta property='og:type' content='website' />
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:title' content={title} />
      <meta name='twitter:description' content={description} />
    </Helmet>
  );
}
